/**
 * ShimmerLogo Component
 *
 * A premium loading animation for the Motito logo featuring:
 * - Glow pulse effect (logo "breathing" with soft glow)
 * - Shimmer sweep (light moving across the logo)
 *
 * Used in loading screens and splash pages for a modern, premium feel.
 */

import { cn } from '@/libs/utils';

interface ShimmerLogoProps {
  className?: string;
  size?: 'sm' | 'md' | 'lg' | 'bootstrap';
  showShimmer?: boolean;
  animate?: boolean;
}

const sizeClasses = {
  sm: 'h-8',
  md: 'h-12',
  lg: 'h-16',
  bootstrap: 'h-[52px]',
};

export function ShimmerLogo({
  className,
  size = 'md',
  showShimmer = true,
  animate = true,
}: ShimmerLogoProps) {
  return (
    <div className={cn('relative inline-block', className)}>
      {/* Logo with glow pulse animation */}
      <img
        src="/assets/svgs/motito.svg"
        alt="Motito"
        className={cn(
          'w-auto',
          animate ? 'animate-logo-breathe' : undefined,
          sizeClasses[size]
        )}
      />

      {/* Shimmer overlay - light sweep effect */}
      {showShimmer && (
        <div
          className="pointer-events-none absolute inset-0 overflow-hidden"
          aria-hidden="true"
        >
          <div
            className="absolute inset-0 animate-shimmer"
            style={{
              background:
                'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.4) 50%, transparent 100%)',
              width: '100%',
            }}
          />
        </div>
      )}
    </div>
  );
}

/**
 * Alternative: Inline SVG version with direct animation control
 * Use this if you need more control over the SVG fill color or animation
 */
export function ShimmerLogoSVG({
  className,
  size = 'md',
  animate = true,
  fillColor = '#0046C8',
  strokeColor = fillColor,
}: Omit<ShimmerLogoProps, 'showShimmer'> & {
  fillColor?: string;
  strokeColor?: string;
}) {
  return (
    <div className={cn('relative inline-block', className)}>
      <svg
        viewBox="0 0 327 86"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={cn('w-auto', animate ? 'animate-logo-breathe' : undefined, sizeClasses[size])}
      >
        <path
          d="M127.657 26.4749C143.467 26.4749 156.883 37.6964 156.883 55.3557C156.883 72.9966 143.486 84.5813 127.657 84.5813C111.822 84.5811 98.5469 72.9905 98.5469 55.3557C98.547 37.7024 111.842 26.4751 127.657 26.4749ZM296.213 26.4749C312.022 26.4749 325.438 37.6964 325.438 55.3557C325.438 72.9966 312.042 84.5813 296.213 84.5813C280.378 84.5813 267.103 72.9907 267.103 55.3557C267.103 37.7023 280.397 26.4749 296.213 26.4749ZM65.8994 26.4749C72.861 26.4749 78.3669 28.7164 82.1113 33.0618C85.8323 37.38 87.6758 43.6161 87.6758 51.345V83.4348H74.9541V52.0325C74.9541 47.8005 73.8819 44.543 72.0195 42.3616C70.1781 40.2049 67.4516 38.9671 63.8369 38.967C59.7327 38.967 56.5001 40.5025 54.2812 43.0198C52.049 45.5523 50.7715 49.1628 50.7715 53.4075V83.4348H38.0498V51.9182C38.0498 47.6938 36.9522 44.455 35.1494 42.2942C33.3653 40.1559 30.8049 38.967 27.6201 38.967C24.1054 38.9671 20.6761 39.9948 18.1406 42.2571C15.6294 44.4978 13.8672 48.0627 13.8672 53.4075V83.4348H1.14551V27.6204H13.8672V33.0305C15.3329 31.4091 17.1407 30.1461 18.9873 29.1946C21.933 27.6767 25.0899 26.8909 27.416 26.7073L27.4287 26.7063L27.4424 26.7053C29.573 26.587 31.6008 26.7041 33.5225 27.0608H33.5234C39.8624 28.0278 44.5545 31.1344 47.4062 36.1946C52.7107 27.838 60.8935 26.4749 65.8994 26.4749ZM185.203 7.44946V27.6204H196.549V38.7375H185.203V83.4348H172.481V38.7375H162.854V27.6204H172.481V7.44946H185.203ZM218.683 27.6204V83.4348H206.076V27.6204H218.683ZM250.565 7.44946V27.6204H261.912V38.7375H250.565V83.4348H237.844V38.7375H228.217V27.6204H237.844V7.44946H250.565ZM127.657 38.967C118.493 38.9673 111.154 45.2698 111.154 55.3557C111.154 65.7006 118.524 72.2032 127.657 72.2034C136.571 72.2034 144.161 65.6913 144.161 55.3557C144.161 45.2789 136.602 38.967 127.657 38.967ZM296.213 38.967C287.049 38.967 279.709 45.2696 279.709 55.3557C279.709 65.7008 287.079 72.2034 296.213 72.2034C305.127 72.2034 312.717 65.6913 312.717 55.3557C312.717 45.2789 305.158 38.967 296.213 38.967ZM212.38 1.14575C216.922 1.14598 220.746 4.31299 220.746 9.05396C220.746 13.795 216.922 16.9619 212.38 16.9622C207.837 16.9622 204.013 13.7952 204.013 9.05396C204.013 4.31282 207.837 1.14575 212.38 1.14575Z"
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth="2.29217"
        />
      </svg>

      {/* Shimmer overlay */}
      <div
        className="pointer-events-none absolute inset-0 overflow-hidden"
        aria-hidden="true"
      >
        <div
          className="absolute inset-0 animate-shimmer"
          style={{
            background:
              'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.5) 50%, transparent 100%)',
            width: '100%',
          }}
        />
      </div>
    </div>
  );
}

export default ShimmerLogo;
